<template>
  <div class="y-filter-travelers-selector">
    <y-text
      v-if="showLabel"
      class="title"
      font="Barlow, Barlow"
      color="#949494"
      size="14px"
    >
      {{ t("shared.hotel.search.travelers") }}
    </y-text>
    <el-popover placement="bottom" :width="400" trigger="click">
      <template #reference>
        <div>
          <font-icon class="people-icon ren-shu" icon="icon-renshu" />
          <y-text font="var(--y-medium-font)" size="18px" color="#292929">
            {{ guest.numberOfRooms }}
            {{
              guest.numberOfRooms > 1
                ? t(
                    `shared.hotel.search${
                      channelHl ? ".hotelux.rooms" : ".rooms"
                    }`
                  )
                : t(
                    `shared.hotel.search${
                      channelHl ? ".hotelux.room" : ".room"
                    }`
                  )
            }}, {{ guest.numberOfAdults }}
            {{
              guest.numberOfAdults > 1
                ? t("shared.hotel.search.adults")
                : t("shared.hotel.search.adult")
            }}
          </y-text>
        </div>
      </template>
      <div class="travel-content">
        <div class="travel-item">
          <y-text color="#292929" font="var(--y-medium-font)" size="16px">{{
            t("shared.hotel.search.ROOMS")
          }}</y-text>
          <div class="number">
            <font-icon
              size="18"
              :class="{ disable: roomReduceDisabled }"
              class="people-jianshao people-icon"
              icon="icon-quanjian"
              @click="handleRoomCountChange(-1)"
            />
            <y-text
              class="count"
              color="#292929"
              font="var(--y-medium-font)"
              size="24px"
              >{{ guest.numberOfRooms }}</y-text
            >
            <font-icon
              size="18"
              :class="{ disable: roomAddDisabled }"
              class="people-tianjia people-icon"
              icon="icon-quanjia"
              @click="handleRoomCountChange(1)"
            />
          </div>
        </div>
        <el-divider color="#eee" />
        <div class="travel-item">
          <y-text color="#292929" font="var(--y-medium-font)" size="16px">{{
            t("shared.hotel.search.ADULTS") + " (per room)"
          }}</y-text>
          <div class="number">
            <font-icon
              size="18"
              :class="{ disable: adultReduceDisabled }"
              class="people-jianshao people-icon"
              icon="icon-quanjian"
              @click="handleAdultCountChange(-1)"
            />
            <y-text
              class="count"
              color="#292929"
              font="var(--y-medium-font)"
              size="24px"
              >{{ guest.numberOfAdults }}</y-text
            >
            <font-icon
              size="18"
              :class="{ disable: adultAddDisabled }"
              class="people-tianjia people-icon"
              icon="icon-quanjia"
              @click="handleAdultCountChange(1)"
            />
          </div>
        </div>
      </div>
    </el-popover>
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import { useYChannelStoreHook } from "@/distributions/consumer/y/store/channel";
import { useChannelStoreHook } from "@/store/modules/channel";
const { t } = useI18n();
const { channel } = useChannelStoreHook();
const emit = defineEmits(["update:modelValue", "change"]);
const props = defineProps({
  showLabel: {
    type: Boolean,
    default: true
  },
  modelValue: {
    type: Object
  }
});

const guest = ref({
  numberOfRooms: ref(props.modelValue?.numberOfRooms),
  numberOfAdults: ref(props.modelValue?.numberOfAdults)
});

const roomReduceDisabled = computed(() => {
  return (
    guest.value.numberOfRooms <= 1 ||
    channel?.features?.tpHotel?.numberOfRooms <= 1
  );
});

const channelHl = computed(() => {
  const yChannelStore = useYChannelStoreHook();

  return (
    yChannelStore.channel === "hotelux" || yChannelStore.channel === "hoteluxjp"
  );
});

const roomAddDisabled = computed(() => {
  return (
    guest.value.numberOfRooms >= channel?.features?.tpHotel?.numberOfRooms ||
    channel?.features?.tpHotel?.numberOfRooms <= 1
  );
});

const adultReduceDisabled = computed(() => {
  return guest.value.numberOfAdults <= 1;
});
const adultAddDisabled = computed(() => {
  return guest.value.numberOfAdults >= 4;
});

const handleRoomCountChange = value => {
  if (value > 0) {
    if (roomAddDisabled.value) return;
  }
  if (value < 0) {
    if (roomReduceDisabled.value) return;
  }
  let num = guest.value.numberOfRooms * 1;
  num += value;
  guest.value.numberOfRooms = num;
  emit("update:modelValue", guest.value);
  emit("change", {
    numberOfRooms: guest.value.numberOfRooms,
    numberOfAdults: guest.value.numberOfAdults
  });
};
const handleAdultCountChange = value => {
  if (value > 0) {
    if (adultAddDisabled.value) return;
  }
  if (value < 0) {
    if (adultReduceDisabled.value) return;
  }
  let num = guest.value.numberOfAdults * 1;
  num += value;
  guest.value.numberOfAdults = num;
  emit("update:modelValue", guest.value);
  emit("change", {
    numberOfRooms: guest.value.numberOfRooms,
    numberOfAdults: guest.value.numberOfAdults
  });
};

// Watch for changes in the modelValue prop to update guest
watch(
  () => props.modelValue,
  newVal => {
    if (newVal) {
      guest.value.numberOfRooms = newVal.numberOfRooms || 1;
      guest.value.numberOfAdults = newVal.numberOfAdults || 1;
    }
  },
  { immediate: true }
);
</script>

<style lang="scss" scoped>
.y-filter-travelers-selector {
  .title {
    margin-bottom: 8px;
    line-height: 1;
    display: block;
  }
  .people-icon {
    font-size: 18px;
    margin-right: 11px;
  }
}
:deep(.el-divider--horizontal) {
  --el-border-color: #eee;
  margin: 40px 0;
}
.travel-content {
  padding: 36px 12px;
  .travel-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    .number {
      display: flex;
      align-items: center;
      .people-jianshao,
      .people-tianjia {
        font-size: 24px;
        cursor: pointer;
      }
      .count {
        padding: 0 40px;
      }
    }
  }
}
.disable {
  color: #d8d8d8;
}
.ren-shu {
  font-size: 20px;
}
</style>
